/**
 * @poc-to-production
 *
 * Project catalog — one entry per demo project. Each entry bundles
 * everything the UI needs to render that project:
 *
 *   - The "deduction story" content (name, location, code basis,
 *     project type, deduction trace) shown in the Requirements
 *     Intelligence panel.
 *   - The inferred jurisdictions (which rule packs apply).
 *   - The model bundle URLs the engine/viewer/properties stores load.
 *   - The sheet list the SheetPicker renders.
 *   - A per-ontology-type default sheet so engine-derived violations
 *     point at the right F2D when clicked.
 *
 * In production, project metadata comes from the ACC / AEC DM
 * lookup; sheets come from the model's published views. Today
 * everything's a hand-authored fixture and the SVF bundles are
 * pre-downloaded into `apps/web/public/svf/<id>/`.
 *
 * Two projects ship today:
 *   - snowdon-towers (rac-basic SVF) — NYC mixed-use, the Phase 1
 *     demo we've been pitching all along.
 *   - casita-blanca — a real LA ADU model (gitignored bundle) with
 *     the 10 CA CRC 2022 rules running against real Revit data.
 */

import type { SheetInfo } from "./sheets";

export interface DeductionSignal {
  label: string;
  value: string;
}

/**
 * Manual review item the architect should verify on a sheet that
 * Vera doesn't auto-check. Surfaces in SheetChecklistCard so the
 * demo stays honest about scope while still positioning Vera as a
 * collaborator: "here's what's required on this sheet that you
 * still need to eyeball."
 */
export interface ChecklistItem {
  /** Short label, e.g. "Front setback ≥ 15 ft". */
  label: string;
  /** Optional code/regulation citation, e.g. "LAMC §12.22 A.33". */
  cite?: string;
}

export interface Project {
  /** Stable id used by stores + URL hashing. */
  id: string;
  /** Project name shown in headers / crumbs. */
  projectName: string;
  /** Short phase/package descriptor under the project name. */
  packageLabel: string;
  location: {
    addressLine: string;
    city: string;
    stateCode: string;
    postalCode: string;
  };
  codeBasis: string;
  projectType: string;
  phase: string;
  metadataSource: string;
  modelSource: string;
  /** Signals shown in the "Why this jurisdiction?" trace. */
  deductionTrace: DeductionSignal[];
  /** Jurisdiction IDs Vera deduced as applicable for this project. */
  inferredJurisdictionIds: string[];
  /**
   * URLs loaded by the engine/properties stores. Paths are
   * absolute-from-web-root so they work in both standalone and Forma
   * shells without re-resolving against a base.
   */
  elementFactsUrl: string;
  propertiesUrl: string;
  /** Sheets registered with the picker / viewer. */
  sheets: SheetInfo[];
  /** Sheet to load when the project becomes active. */
  defaultSheetId: string;
  /**
   * Default sheet to surface each engine-derived violation on,
   * keyed by ontology class. Replaces the static map that used to
   * live inside engineStore.
   */
  defaultSheetByOntologyType: Record<string, string>;
  /**
   * Manual review checklist keyed by sheet id. Sheets present in
   * this map will surface a "manual review checklist" card to the
   * architect while that sheet is the active one — used for sheets
   * Vera doesn't auto-check (site plan, roof plan, project-info).
   * Sheets where Vera does run rules (e.g. the floor plan) should
   * NOT have an entry — the violations panel itself is the audit
   * trail.
   */
  manualChecklistBySheet?: Record<string, ChecklistItem[]>;
}

/** rac-basic sheet set — the Snowdon Towers demo bundle. */
const RAC_BASIC_ROOT = "/svf/rac-basic";
const RAC_BASIC_SHEETS: SheetInfo[] = [
  {
    id: "a001-title",
    name: "A001 - Title Sheet",
    documentPath: `${RAC_BASIC_ROOT}/620d061c-65d0-e418-6a7f-6c891b6d27b7_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "a101-site-plan",
    name: "A101 - Site Plan",
    documentPath: `${RAC_BASIC_ROOT}/4eac2166-f032-ad91-b7a2-11801b128bba_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "a102-plans",
    name: "A102 - Plans",
    documentPath: `${RAC_BASIC_ROOT}/8ae736e0-7646-1946-1aad-d09d598a5493_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "a103-elev-sections",
    name: "A103 - Elevations/Sections",
    documentPath: `${RAC_BASIC_ROOT}/3e20972e-1742-3cea-a51f-940b9d3971ce_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "a104-elev-sec-det",
    name: "A104 - Elev./Sec./Det.",
    documentPath: `${RAC_BASIC_ROOT}/2d768de6-5451-6601-0e31-53c0f3699862_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "a105-stair-sections",
    name: "A105 - Elev./Stair Sections",
    documentPath: `${RAC_BASIC_ROOT}/d9a9a323-3cc3-1838-9a3f-5e8cd3e66859_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "view-3d",
    name: "{3D} View",
    documentPath: `${RAC_BASIC_ROOT}/Resource/3D_View/_3D_/_3D_.svf`,
    kind: "3d",
  },
];

/**
 * Casita Blanca sheet set.
 *
 * Both 2D F2Ds and 3D views are wired. The 9 F2D titles below come
 * verbatim from `apps/web/public/svf/casita-blanca/sheets.json`
 * (produced by the ingestion thread from the APS Model Derivative
 * manifest's 2D nodes — keep that file the source of truth and edit
 * here only when sheets.json changes).
 *
 * The "A2.1 - FLOOR PLAN" sheet is the default — it's the main
 * floor plan for this single-storey ADU and is what most CRC R310 /
 * R311 / R312 element violations want to be shown on.
 *
 * Path-hygiene note: resolved upstream as of 2026-06-02. The
 * ingestion thread updated `tools/download-svf.mjs` to normalise
 * output paths (space → underscore, strip `{}` from .svf names).
 * Re-pulls now produce a viewer-clean bundle out of the box:
 *
 *   Resource/3D_View/<uuid>/3D_-_<design-study-a>.svf   ✓
 *   Resource/3D_View/<uuid>/3D_-_<design-study-b>.svf   ✓
 *   Resource/3D_View/<uuid-1>/<uuid-2>/New_Construction.svf ✓
 *
 * F2D folders already used clean UUID names so they were never
 * affected.
 */
const CASITA_ROOT = "/svf/casita-blanca";
const CASITA_3D_VIEWS_DIR = `${CASITA_ROOT}/Resource/3D_View`;
const CASITA_SHEETS: SheetInfo[] = [
  // Standard ADU permit set — A0.1 / A1.0 / A2.1 / A2.2. The four
  // design-study variants ("Documentation Level 01/02", "Floor Plan
  // Option A/B") are submission-internal and skipped here so the
  // picker matches what an LA County reviewer actually expects to
  // see. A2.1 - FLOOR PLAN is the default — it's the only sheet
  // Vera auto-checks today; the others get a manual checklist via
  // SheetChecklistCard.
  {
    id: "casita-a0-1",
    name: "A0.1 - PROJECT INFORMATION",
    documentPath: `${CASITA_ROOT}/a200f73a-f243-d48f-0ab5-e5e2d1330a03_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "casita-a1-0",
    name: "A1.0 - SITE PLAN",
    documentPath: `${CASITA_ROOT}/811ef9e8-101f-1302-452f-2fd94f8f2b45_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "casita-a2-1",
    name: "A2.1 - FLOOR PLAN",
    documentPath: `${CASITA_ROOT}/55197801-2fb5-586e-9c24-3325be42059c_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  {
    id: "casita-a2-2",
    name: "A2.2 - ROOF PLAN",
    documentPath: `${CASITA_ROOT}/3b83472d-5742-58be-0294-4f497630e890_f2d/primaryGraphics.f2d`,
    kind: "2d",
  },
  // Single full-building 3D export ({3D} New Construction in Revit) — complete
  // geometry including plumbing fixtures for BCA ray-march. Two internal
  // design-study 3D views were dropped from the picker — they are partial
  // exports without reliable toilet meshes.
  {
    id: "casita-3d-model",
    name: "3D Model — full building",
    documentPath: `${CASITA_3D_VIEWS_DIR}/08f99ae5-b8be-4f8d-881b-128675723c10/59512f3d-eb22-4843-9f91-77273b6d0ae3-00197ea3/New_Construction.svf`,
    kind: "3d",
  },
];

export const PROJECTS: Project[] = [
  {
    id: "snowdon-towers",
    projectName: "Sustainable Villa — Mixed-Use Permit Package",
    packageLabel: "Permit package assembly",
    location: {
      addressLine: "350 Fifth Avenue",
      city: "New York",
      stateCode: "NY",
      postalCode: "10118",
    },
    codeBasis: "2022 NYC Building Code (on 2018 IBC base)",
    projectType: "Mixed-use residential over retail",
    phase: "Permit package assembly",
    metadataSource: "ACC project + AEC DM hub region",
    modelSource: "Revit 2024 model (rac-basic, sealed by NY-licensed AOR)",
    deductionTrace: [
      {
        label: "ACC project address",
        value: "350 Fifth Ave, New York, NY 10118",
      },
      { label: "AEC DM hub region", value: "Americas / US-NY (en-US)" },
      { label: "Model unit system", value: "Imperial (feet & inches)" },
      {
        label: "Architect-of-record license",
        value: "NY State #047XXX (active)",
      },
      { label: "Project type tag (ACC)", value: "Mixed-use · R-2 over M" },
    ],
    inferredJurisdictionIds: ["us-ny-nyc"],
    elementFactsUrl: "/element-facts.json",
    propertiesUrl: "/svf/rac-basic/properties.json",
    sheets: RAC_BASIC_SHEETS,
    defaultSheetId: "a102-plans",
    defaultSheetByOntologyType: {
      "vera:Door": "a102-plans",
      "vera:Opening": "a102-plans",
      "vera:Stair": "a105-stair-sections",
      "vera:Handrail": "a105-stair-sections",
      "vera:Guard": "a105-stair-sections",
    },
  },
  {
    id: "casita-blanca",
    projectName: "Casita Blanca — ADU",
    packageLabel: "Real ADU model, ingested 2026-06-01",
    location: {
      addressLine: "Confidential (customer model)",
      city: "Los Angeles",
      stateCode: "CA",
      postalCode: "—",
    },
    codeBasis:
      "2022 California Residential Code (Title 24 Part 2.5, LA County)",
    projectType: "Detached single-family ADU",
    phase: "Design / permit package assembly",
    metadataSource: "APS Model Derivative + AEC DM hub region",
    modelSource:
      "Real customer Revit model (gitignored — confidential; bundle at /svf/casita-blanca/)",
    deductionTrace: [
      {
        label: "APS Model Derivative",
        value: "9 F2D sheets + 3 3D views, property DB ingested",
      },
      { label: "AEC DM hub region", value: "Americas / US-CA" },
      { label: "Model unit system", value: "Imperial (decimal feet)" },
      {
        label: "Project type tag",
        value: "Detached ADU (Group R-3)",
      },
      {
        label: "Element coverage",
        value: "39 mapped elements (13 doors / 5 stairs / 21 openings)",
      },
    ],
    inferredJurisdictionIds: ["us-ca-la-county"],
    elementFactsUrl: "/svf/casita-blanca/element-facts.json",
    propertiesUrl: "/svf/casita-blanca/properties.json",
    sheets: CASITA_SHEETS,
    // A2.1 FLOOR PLAN is the main architectural sheet for this
    // single-storey ADU — doors, openings, and stair plans live
    // here. The 3D views are listed below the F2Ds in the picker
    // and remain selectable when a user wants the full model view.
    defaultSheetId: "casita-a2-1",
    // Doors + windows render cleanly in A2.1 FLOOR PLAN — `viewer
    // .select([dbId])` highlights them as a swing-arc + leaf line
    // (doors) or a wall-break line (windows). Stairs are different:
    // Casita's Revit model maps the stair to "Monolithic Run" dbIds
    // whose plan-view 2D bbox isn't directly hit-testable from the
    // F2D — clicking a stair violation looks like nothing happened.
    // Routing stair/handrail/guard classes to the 3D view (where
    // every dbId has resolvable geometry) gives the user a working
    // click target. The auto-switch fires only when the user is on
    // a non-universal sheet (see ViolationsPanel onClickViolation),
    // so manual sheet picks aren't fought.
    defaultSheetByOntologyType: {
      "vera:Door": "casita-a2-1",
      "vera:Opening": "casita-a2-1",
      "vera:Room": "casita-a2-1",
      "vera:Stair": "casita-3d-model",
      "vera:Handrail": "casita-3d-model",
      "vera:Guard": "casita-3d-model",
      "vera:AccessibleWaterCloset": "casita-3d-model",
    },
    // Sheets Vera doesn't auto-check (yet) get a manual checklist
    // so the architect knows what an LA County reviewer will look
    // for. A2.1 is intentionally omitted — the violations panel is
    // the audit trail for what Vera covers there. Items are
    // representative, not exhaustive; cites point at the most
    // commonly-asked rule for each line.
    manualChecklistBySheet: {
      "casita-a0-1": [
        {
          label: "Code analysis: applicable codes + edition cited",
          cite: "CRC §R104",
        },
        {
          label: "Occupancy classification stated (R-3 detached ADU)",
          cite: "CRC §R101.2",
        },
        { label: "Construction type + sprinkler status", cite: "CRC §R313" },
        {
          label: "Climate zone + energy compliance pathway",
          cite: "Title 24 Part 6",
        },
        { label: "Sheet index matches submitted set" },
        { label: "Vicinity map with project address" },
        { label: "Designer of record + stamp" },
      ],
      "casita-a1-0": [
        { label: "Lot dimensions + north arrow + scale" },
        {
          label: "Front setback ≥ 15 ft from primary street",
          cite: "LAMC §12.22 A.33",
        },
        {
          label: "Side/rear setbacks meet R1 minimums or ADU-specific overlay",
          cite: "LAMC §12.22 A.33(c)",
        },
        {
          label: "ADU separation from main dwelling (if detached)",
          cite: "CRC §R302.1",
        },
        {
          label: "Lot coverage % shown + within zoning limit",
          cite: "LAMC §12.21.1",
        },
        { label: "Paved areas, driveway, easements drawn" },
        { label: "Existing trees + protected-species notation (if any)" },
      ],
      "casita-a2-2": [
        {
          label: "Roof slope ≥ 1/4 in. per ft for drainage",
          cite: "CRC §R903.3",
        },
        { label: "Ridge / hip / valley locations + slope directions" },
        { label: "Downspout / scupper / overflow locations" },
        {
          label:
            "Ventilation: net free area ratio meets attic vent requirement",
          cite: "CRC §R806.1",
        },
        {
          label: "Solar-ready zone (if applicable to occupancy)",
          cite: "Title 24 Part 6 §110.10",
        },
      ],
    },
  },
];

// Casita Blanca is the default — real customer ADU model running
// against real CA CRC rules with one confirmed sill-height violation
// to anchor the pitch. Snowdon Towers (rac-basic + NYC BC) stays in
// the project switcher as a second story.
export const DEFAULT_PROJECT_ID = "casita-blanca";

export function getProject(id: string): Project {
  const p = PROJECTS.find(p => p.id === id);
  if (!p) throw new Error(`Unknown project id: ${id}`);
  return p;
}
